<template>
  <div
    v-if="value"
    class="relative w-full overflow-hidden rounded border border-dashed border-primary"
  >
    <img
      class="max-h-[290px] w-full"
      :src="value.includes('data') ? value : `/uploads/${value}`"
      :alt="__('Company cover image')"
    />

    <button
      class="absolute inset-0 flex items-center justify-center bg-zinc-800/75 opacity-0 transition-opacity hover:opacity-100 focus:opacity-100"
      type="button"
      :aria-label="__('Delete')"
      @click="handleDeleteButtonClick"
    >
      <Icon class="text-xl text-white" name="delete" />
    </button>
  </div>

  <div v-else>
    <label
      :class="`${isDragging ? 'bg-primary/5' : 'bg-zinc-50'} flex h-40 w-full cursor-pointer flex-col items-center justify-center rounded border border-dashed border-primary p-4 transition-colors`"
      for="upload-cover"
      @drop.prevent="handleDrop"
      @dragover.prevent="handleDragOver"
      @dragleave.prevent="handleDragLeave"
    >
      <Icon class="text-2xl text-primary" name="backup" is-filled />

      <span class="mt-1 block text-center text-sm font-medium text-primary">
        {{ __('Upload cover') }}
      </span>
      <span
        class="mt-2.5 block text-center text-xs leading-normal text-zinc-600"
      >
        {{ __('Click to upload or drag and drop') }}
      </span>
      <span class="mt-1 block text-center text-xs leading-normal text-zinc-600">
        {{ `JPG, JPEG ${__('or')} PNG (max. 2 MB)` }}
      </span>
    </label>

    <input
      id="upload-cover"
      class="sr-only"
      type="file"
      :accept="extensions.join(',')"
      @change="handleInputChange"
    />

    <ImageCropper
      v-model:open="isCropperOpen"
      v-model:value="base64"
      :width="width"
      :height="height"
      :aspect-ratio="width / height"
      @change="handleCropperChange"
      @update:errors="handleUpdateErrors"
      @update:value="handleUpdateValue"
    />
  </div>
</template>

<script setup>
  import { ref } from 'vue';
  import { __ } from '@/helpers';
  import { Icon } from '@/components/block/icon';
  import { ImageCropper } from '@/components/other/image-cropper';

  const props = defineProps({
    value: {
      type: [String, null],
      required: true
    },
    file: {
      type: [File, null],
      required: true
    },
    errors: {
      type: Array,
      default: () => []
    }
  });

  const emits = defineEmits(['update:value', 'update:file', 'update:errors']);

  const width = 1116;
  const height = 191;
  const maxFileSize = 2;
  const extensions = ['.jpg', '.jpeg', '.png'];

  const base64 = ref('');
  const isCropperOpen = ref(false);
  const isDragging = ref(false);

  function readFiles(files) {
    if (!files || !files.length) {
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      base64.value = reader.result;
      isCropperOpen.value = true;
    };

    reader.readAsDataURL(files[0]);
  }

  function handleInputChange(event) {
    readFiles(event.target.files);
    event.target.value = '';
  }

  function handleDrop(event) {
    const files = Array.from(event.dataTransfer.files);

    const validFiles = files.filter((file) =>
      extensions
        .map((extension) => `image/${extension.split('.')[1]}`)
        .includes(file.type)
    );

    if (validFiles.length) {
      readFiles(validFiles);
    }

    isDragging.value = false;
  }

  function handleDragOver() {
    isDragging.value = true;
  }

  function handleDragLeave() {
    isDragging.value = false;
  }

  function handleDeleteButtonClick() {
    emits('update:value', null);
    emits('update:file', null);
    emits('update:errors', []);
  }

  function handleCropperChange(base64, file) {
    if (file.size > maxFileSize * 1024 * 1024) {
      emits('update:errors', [
        `${__('File size exceeds limit. Please upload a file smaller than')} ${maxFileSize} MB.`
      ]);
      return;
    }

    emits('update:value', base64);
    emits('update:file', file);
    emits('update:errors', []);
  }

  function handleUpdateErrors(value) {
    emits('update:errors', value);
  }

  function handleUpdateValue(value) {
    emits('update:value', value);
  }

</script>
